import React, { useState, useRef, useEffect } from "react";
import { X, Upload, FileSpreadsheet, Trash2 } from "lucide-react";
import Papa from "papaparse";
import { useNavigate } from "react-router-dom";
import FormData from "form-data";
import axios from "axios";
import apiClient from "../utils/appClient";
import { downloadTemplate } from "../utils/downloadTemplateUtils";
import { useProjectContext } from "../context/ProjectContext";
import { useCbomContext } from "../context/CbomContext";
import { uploadSensorAgent } from "../services/apiServices";
import { TailSpin } from "react-loader-spinner";

interface ImportedFile {
  file: File;
  name: string;
  progress: number;
  size: number;
}
const ImportModal: React.FC = () => {
  const { setShowAgentUpload, isAgentUploaded, setIsAgentUploaded } =
    useCbomContext();
  const navigate = useNavigate();
  const [dragActive, setDragActive] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importedFiles, setImportedFiles] = useState<ImportedFile[]>([]);
  const uploadIntervalsRef = useRef<NodeJS.Timeout[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const allFilesUploaded =
    importedFiles.length > 0 &&
    importedFiles.every((file) => file.progress === 100);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };
  const formatFileSize = (size: number) => {
    if (size < 1024) return `${size} bytes`;
    const kb = size / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };
  const handleFiles = (files: File[]) => {
    const newFiles = files.map((file) => ({
      file,
      name: file.name,
      progress: 0,
      size: file.size,
    }));
    setImportedFiles(newFiles);

    // Stop any previous uploads
    uploadIntervalsRef.current.forEach(clearInterval);
    uploadIntervalsRef.current = [];

    // Simulate upload progress
    newFiles.forEach((file, index) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += 5;
        if (progress <= 100) {
          setImportedFiles((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], progress };
            return updated;
          });
        } else {
          clearInterval(interval);
        }
      }, 200);
      uploadIntervalsRef.current.push(interval);
    });
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("Files Selected:", e.target.files);
    if (e.target.files) {
      setSelectedFile(e.target.files[0]);
      const files = Array.from(e.target.files);
      handleFiles(files);
    }
  };

  const handleSkipButtonClick = () => {
    uploadIntervalsRef.current.forEach(clearInterval);
    uploadIntervalsRef.current = [];
    setImportedFiles([]);
  };
  const handleFileUpload = async () => {
    if (!selectedFile) return;
    try {
      setIsLoading(true);
      await uploadSensorAgent(selectedFile, setIsAgentUploaded);
      // isAgentUploaded && setShowAgentUpload(false);
      setTimeout(() => {
        if (isAgentUploaded) {
          setShowAgentUpload(false);
        }
      }, 10);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="p-10 fixed inset-0 z-20 bg-gray-600 bg-opacity-50 overflow-hidden w-screen h-screen flex items-center justify-center">
      <div className="w-[800px] bg-custom-blue0 shadow flex flex-col items-center justify-center p-2 rounded-lg m-8">
        <div className="w-full flex justify-between items-center mb-4">
          <h1 className="text-xl font-bold text-custom-blue7 pl-2">
            Upload Files
          </h1>
          <button
            className="p-2 bg-custom-blue7 rounded-full"
            onClick={() => {
              setShowAgentUpload(false);
            }}
          >
            <X size={20} className="text-gray-50" />
          </button>
        </div>
        <div className="w-full max-w-2xl bg-gray-50 rounded-lg shadow-sm p-6">
          {/* Upload Section */}
          {importedFiles.length === 0 ? (
            <>
              <div className="mb-8 bg-white">
                <div
                  className={`relative rounded-lg shadow-lg p-8 text-center
                    ${
                      dragActive
                        ? "border-custom-blue4 bg-custom-blue2"
                        : "border-custom-blue6"
                    }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    onChange={handleFileChange}
                    accept=".zip,.tcz"
                    multiple
                  />
                  <div className="flex flex-col items-center gap-4">
                    <Upload className="w-12 h-12 text-custom-blue6" />
                    <div>
                      <span className="text-gray-600">
                        Drop your file(s) or{" "}
                      </span>
                      <label
                        htmlFor="file-upload"
                        className="text-custom-blue6 cursor-pointer transition"
                      >
                        Browse Files
                      </label>
                    </div>
                    <p className="text-sm text-custom-blue6">
                      *Please ensure that you upload your file in ZIP format.
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* Imported Files Section */
            <>
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold text-custom-blue7">
                    Imported Files
                  </h2>
                </div>
                {importedFiles.length > 1 && allFilesUploaded && (
                  <div className="flex justify-end mb-4 ">
                    <button
                      className="px-2 py-2 bg-custom-veryhigh text-white rounded-lg"
                      onClick={() => setImportedFiles([])}
                    >
                      Delete All Files
                    </button>
                  </div>
                )}
                {importedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg shadow-lg p-4 mb-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <FileSpreadsheet className="w-6 h-6 text-green-600" />
                        <span className="text-sm font-medium text-gray-600">
                          {file.name}
                        </span>
                      </div>
                      {allFilesUploaded && (
                        <div className="flex space-x-12">
                          <span className="text-gray-500">
                            {formatFileSize(file.size)}
                          </span>
                          <button
                            className="p-1 bg-custom-blue5 rounded-full"
                            onClick={() => {
                              setImportedFiles((prev) =>
                                prev.filter((_, i) => i !== index)
                              );
                            }}
                          >
                            <Trash2
                              size={20}
                              className="bg-custom-blue5 rounded-full text-white cursor-pointer"
                            />
                          </button>
                        </div>
                      )}
                    </div>
                    {!allFilesUploaded && (
                      <>
                        <div className="relative w-full h-2 bg-gray-200 rounded-full">
                          <div
                            className="absolute left-0 top-0 h-full bg-custom-blue4 rounded-full transition-all duration-300"
                            style={{ width: `${file.progress}%` }}
                          />
                        </div>
                        <div className="text-right mt-1">
                          <span className="text-sm text-gray-500">
                            {file.progress}%{" "}
                            {file.progress < 100 ? "Uploading..." : "Complete"}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-center items-center gap-6 mt-6">
                <button
                  className="px-6 py-2 text-white bg-custom-blue5 hover:bg-gray-200 rounded-lg transition-colors"
                  onClick={() => handleSkipButtonClick()}
                >
                  Cancel
                </button>
                {allFilesUploaded && (
                  <button
                    disabled={isLoading}
                    className="flex items-center justify-center px-6 py-2 text-white bg-custom-blue6 hover:bg-custom-blue7 rounded-lg transition-colors"
                    onClick={handleFileUpload}
                  >
                    {isLoading ? (
                      <>
                        <TailSpin height={17} width={17} color="#fff" />
                        <span className="ml-2">Please wait...</span>
                      </>
                    ) : (
                      "Upload"
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportModal;
